(() => {
function ekoCompareEscapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML;
}

// "Starting at" price in the shopper's market currency. The Control Panel's /compare.json price is the Admin API's
// shop-currency (USD) amount, so showing it as-is was wrong in Canada/UK. Format with the active currency, and refine
// from the storefront's own /products/<handle>.js (market-aware) once it loads.
function ekoCompareMoney(amount) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  const value = Number(amount);
  if (!isFinite(value)) return '';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch (e) {
    return '$' + value.toFixed(value % 1 === 0 ? 0 : 2);
  }
}

function ekoCompareActiveCurrency() {
  return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
}

function ekoComparePriceText(product) {
  // The Control Panel amount is USD: only trust it for a USD market, otherwise wait for the storefront price.
  if (ekoCompareActiveCurrency() !== 'USD' || !product.price) return '';
  return 'Starting at ' + ekoCompareMoney(product.price);
}

class CompareDrawer extends EkoDrawer {
  constructor() {
    super();
    this.cache = {};
  }

  // Always exactly 2 handles for this drawer - the underlying /compare.json endpoint itself
  // accepts any number (see compare.mjs's compareJson), for a future 3+-product page section.
  // currentPageHandle is whichever product's PDP the drawer was opened from (may be neither,
  // if this block is ever shown on an unrelated product) - used to highlight that side's
  // feature blocks so it's obvious which column is "this product" while scrolling.
  open(productAHandle, productBHandle, triggeredBy, currentPageHandle) {
    super.open(triggeredBy);
    this.currentPageHandle = currentPageHandle;
    this.render([productAHandle, productBHandle]);
  }

  async render(handles) {
    const header = this.querySelector('[data-compare-header]');
    const stickyHeader = this.querySelector('[data-drawer-sticky]');
    const body = this.querySelector('[data-compare-body]');
    const cacheKey = handles.join('::');

    if (!this.cache[cacheKey]) {
      header.innerHTML = '';
      stickyHeader.innerHTML = '';
      stickyHeader.classList.remove('is-visible');
      body.innerHTML = '<p class="eko-compare-drawer__loading">Loading comparison&hellip;</p>';

      const endpoint = this.getAttribute('data-endpoint');
      const shop = this.getAttribute('data-shop');
      const params = new URLSearchParams({ products: handles.join(','), shop });

      try {
        const response = await fetch(`${endpoint}?${params.toString()}`);
        this.cache[cacheKey] = await response.json();
      } catch (error) {
        body.innerHTML = '<p class="eko-compare-drawer__error">Comparison is unavailable right now.</p>';
        return;
      }
    }

    this.renderData(this.cache[cacheKey]);
    this.observeStickyReveal();
  }

  // Replace each product's "Starting at" price with the storefront price for the shopper's market.
  localizePrices(products) {
    products.forEach((product) => {
      if (!product.handle) return;
      fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}products/${encodeURIComponent(product.handle)}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => {
          if (!json || !Array.isArray(json.variants) || !json.variants.length) return;
          const cents = Math.min(...json.variants.map((v) => Number(v.price)).filter((n) => isFinite(n)));
          if (!isFinite(cents)) return;
          const text = 'Starting at ' + ekoCompareMoney(cents / 100);
          this.querySelectorAll('[data-price-handle]').forEach((el) => {
            if (el.dataset.priceHandle === product.handle) el.textContent = text;
          });
        })
        .catch(() => {});
    });
  }

  renderData(data) {
    const header = this.querySelector('[data-compare-header]');
    const stickyHeader = this.querySelector('[data-drawer-sticky]');
    const body = this.querySelector('[data-compare-body]');

    const products = Array.isArray(data && data.products) ? data.products : [];
    if (products.length < 2) {
      header.innerHTML = '';
      stickyHeader.innerHTML = '';
      body.innerHTML = '<p class="eko-compare-drawer__error">This comparison isn\'t set up yet.</p>';
      return;
    }

    // Description only appears in the full-size header, not the compact one - the Figma's
    // sticky/condensed state omits it to stay short.
    header.innerHTML = products
      .map(
        (product) => `
          <div class="eko-compare-drawer__product">
            ${product.image ? `<img class="eko-compare-drawer__product-image bg-beige-50" src="${ekoCompareEscapeHtml(product.image)}" alt="${ekoCompareEscapeHtml(product.title)}" loading="lazy">` : ''}
            <div class="eko-compare-drawer__product-title">${ekoCompareEscapeHtml(product.title)}</div>
            ${product.description ? `<div class="eko-compare-drawer__product-description">${ekoCompareEscapeHtml(product.description)}</div>` : ''}
            <div class="eko-compare-drawer__product-price uppercase" data-price-handle="${ekoCompareEscapeHtml(product.handle)}">${ekoCompareEscapeHtml(ekoComparePriceText(product))}</div>
            ${product.url ? `<a class="eko-compare-drawer__product-cta button button-small w-full bg-black" href="${ekoCompareEscapeHtml(product.url)}">Shop Now</a>` : ''}
          </div>`
      )
      .join('');

    // The inner wrapper is what is styled and drawn; the slot itself takes no height (see eko-drawer.css), so revealing it
    // can't push the content down and re-trigger the observer that reveals it (that loop flickered on phones).
    stickyHeader.innerHTML = '<div class="eko-compare-drawer__sticky-inner">' + products
      .map(
        (product) => `
          <div class="eko-compare-drawer__product eko-compare-drawer__product--compact">
            ${product.image ? `<img class="eko-compare-drawer__product-image bg-beige-50" src="${ekoCompareEscapeHtml(product.image)}" alt="${ekoCompareEscapeHtml(product.title)}" loading="lazy">` : ''}
            <div class="eko-compare-drawer__product-title font-bold">${ekoCompareEscapeHtml(product.title)}</div>
            <div class="eko-compare-drawer__product-price uppercase" data-price-handle="${ekoCompareEscapeHtml(product.handle)}">${ekoCompareEscapeHtml(ekoComparePriceText(product))}</div>
            ${product.url ? `<a class="eko-compare-drawer__product-cta button button-small w-full bg-black" href="${ekoCompareEscapeHtml(product.url)}">Shop Now</a>` : ''}
          </div>`
      )
      .join('') + '</div>';

    this.localizePrices(products);

    const categories = Array.isArray(data.categories) ? data.categories : [];
    if (!categories.length) {
      body.innerHTML = '<p class="eko-compare-drawer__error">This comparison isn\'t set up yet.</p>';
      return;
    }

    // Highlights whichever side matches the product the drawer was opened FROM (not the
    // header/category heading above it - just the feature blocks themselves, per Gregory) -
    // -1 (no match) if this block is ever shown on a product that's neither side being compared.
    const currentIndex = products.findIndex((p) => p.handle === this.currentPageHandle);

    // entry.body is rich text (bold/bullet/numbered lists) authored in the Control Panel's
    // own contenteditable editor, not plain text - rendered as real HTML rather than escaped,
    // same trust level as any other admin-authored content on this storefront (the control
    // panel is gated to this app's own admin users, never public input).
    const featureSide = (entry, idx) => `
      <div class="eko-compare-drawer__feature-side${idx === currentIndex ? ' eko-compare-drawer__feature-side--current' : ''}">
        ${entry && entry.icon ? `<img class="eko-compare-drawer__feature-icon" src="${ekoCompareEscapeHtml(entry.icon)}" alt="" loading="lazy">` : ''}
        ${entry && entry.heading ? `<div class="eko-compare-drawer__feature-heading leading-snug">${ekoCompareEscapeHtml(entry.heading)}</div>` : ''}
        ${entry && entry.body ? `<div class="eko-compare-drawer__feature-text">${entry.body}</div>` : ''}
      </div>`;

    body.innerHTML = categories
      .map(
        (category) => `
          <div class="eko-compare-drawer__category">
            <h3 class="eko-compare-drawer__category-name h4">
              ${ekoCompareEscapeHtml(category.name)}
              ${category.subheading ? `<span class="eko-compare-drawer__category-subheading">${ekoCompareEscapeHtml(category.subheading)}</span>` : ''}
            </h3>
            ${(category.features || [])
              .map(
                (feature) => `
                  <div class="eko-compare-drawer__feature">
                    ${(feature.entries || []).map(featureSide).join('')}
                  </div>`
              )
              .join('')}
          </div>`
      )
      .join('');
  }
}

if (!customElements.get('compare-drawer')) customElements.define('compare-drawer', CompareDrawer);
})();
